// Booth screen v0.6 (v0.5 "Tấm gương chung" + Làm đẹp) (design/booth-v2: Booth · BoothShoot · BoothResult).
//
//   ┌ stage 3:4 ───────────────┐  overlay pills: back · partner + latency · photos · room code
//   │  coaching / pose-lock    │  bottom pill: per-person hints, "Chụp luôn", progress
//   └──────────────────────────┘
//   mic · flip · ( shutter ) · Hiệu ứng · mời bạn
//   [effects sheet ~40%: Làm đẹp · Bộ lọc · Nền · Khung · Bố cục]  → the stage shrinks
//
// While shooting the controls give way to a shot tray + stop button; when the
// strip is ready everybody sees the result screen with the real save status.

import './booth.css';
import { h, toast } from './dom';
import { shareInvite } from './share';
import { openViewer } from './viewer';
import type { JoinRequest } from './home';
import { api, finalUrl, toRef, type Session } from '../api/client';
import { remoteConfig, signalingRelays } from '../config';
import { isLoggedIn } from '../auth/oidc';
import { inviteFriendsTo } from './screens/room';
import { shareDialog } from './post';
import { openCamera, type CameraHandle } from '../media/camera';
import { PersonPipeline, createPipeline } from '../media/pipeline';
import { BoothRoom } from '../net/room';
import { installCodecPreferences } from '../net/tuning';
import { DEFAULT_CONFIG, type BoothConfig, type PoseMsg } from '../net/protocol';
import { Stage, type StageLayer } from '../render/stage';
import { RoomGallery, type GalleryItem } from '../booth/gallery';
import { DEFAULT_FACE, NO_ADJUST, sceneSlotOf, type PoseAdjust } from '../booth/layout';
import { ShootCancelled, ShootController } from '../booth/shoot';
import {
  BACKGROUNDS, FILTERS, FRAME_COLORS, LAYOUTS, SEASONAL_BACKGROUNDS, SEASONAL_FRAME_COLORS, STAGE_H, STAGE_W,
  activePacks, drawBackground, drawCover, filterById, layoutById, packDaysLeft, type FramePack,
} from '../booth/themes';
import { icon } from './booth-icons';
import { BEAUTY_PRESETS, SLIDERS, saveBeauty, saveSticker, type BeautySettings } from '../booth/beauty';
import { ATLAS_COLS, ATLAS_ROWS, CELL, STICKERS, drawAtlas } from '../booth/effects';
import { touchFeedback, unlockAudio, buzz } from './booth-coach';
import { createResult, type SaveState } from './booth-result';
import {
  LayoutSync, TEMPLATES, frontOf, newContacts, orderOf, readiness, setFront, setGiver, setMirrorPhoto, setScale, setTemplate, splitConfig,
  stageExtras, swapSlots, withMembers, type InteractionState, type LayoutState,
} from './booth-layout-sync';

/** Custom backgrounds are normalised to this size (3:4) before sharing/storing. */
const BG_W = 1200, BG_H = 1600;
/** long-press on the shutter = "Chụp luôn" (override the pose lock) */
const HOLD_MS = 650;

/** Returns a cleanup function (stops camera + leaves the room) for the router. */
export async function renderBooth(root: HTMLElement, req: JoinRequest, onExit: () => void): Promise<() => void> {
  let current: () => Promise<void> = async () => {};
  const mount = async () => {
    current = await mountBooth(root, req, onExit, async () => {
      await current();
      await mount();
    });
  };
  await mount();
  return () => void current();
}

/** Stand-in camera when getUserMedia fails: the booth still renders and shows the partner. */
async function placeholderCamera(): Promise<CameraHandle> {
  const c = h('canvas', { width: 640, height: 480 });
  const ctx = c.getContext('2d')!;
  const draw = () => {
    ctx.fillStyle = '#1F161B';
    ctx.fillRect(0, 0, c.width, c.height);
  };
  draw();
  const timer = setInterval(draw, 200);
  const stream = c.captureStream(5);
  const track = stream.getVideoTracks()[0];
  const video = h('video', { muted: true, playsInline: true, autoplay: true });
  video.srcObject = stream;
  await video.play().catch(() => {});
  if (!video.videoWidth) {
    await Promise.race([
      new Promise((r) => video.addEventListener('loadedmetadata', r, { once: true })),
      new Promise((r) => setTimeout(r, 1500)),
    ]);
  }
  return {
    video,
    videoTrack: () => track,
    audio: null,
    facing: 'user',
    switchFacing: async () => { throw new Error('chưa mở được camera'); },
    stop: () => { clearInterval(timer); track.stop(); },
  };
}

async function mountBooth(root: HTMLElement, req: JoinRequest, onExit: () => void, remount: () => Promise<void>): Promise<() => Promise<void>> {
  const isHost = req.role === 'host';
  root.replaceChildren(h('main.bth-loading', { role: 'status' }, h('div.spinner'), h('p', {}, 'Đang mở camera và tải mô hình tách nền…')));

  let cam: CameraHandle;
  let camError: string | null = null;
  try {
    cam = await openCamera(true);
  } catch (e) {
    camError = cameraErrorText(e);
    cam = await placeholderCamera();
  }
  const pipe = await createPipeline(cam.video);
  pipe.mode = req.mode;
  try {
    await pipe.start();
  } catch (e) {
    console.error(e);
    if (!camError) toast('Không tải được mô hình AI, chuyển sang chế độ phông xanh', 4000);
    pipe.mode = 'greenscreen';
  }

  let stage: Stage;
  try {
    stage = new Stage();
  } catch (e) {
    toast((e as Error).message, 4000);
    pipe.stop();
    cam.stop();
    onExit();
    return async () => {};
  }
  const extras = stageExtras(stage);

  // outgoing = packed video + mic
  const outStream = new MediaStream([...pipe.stream.getVideoTracks(), ...(cam.audio ? [cam.audio] : [])]);
  if (isLoggedIn()) await api.loadTurn().catch(() => {});
  installCodecPreferences();
  const room = new BoothRoom(req.code, req.role, req.name, outStream, await signalingRelays());

  let config: BoothConfig = { ...DEFAULT_CONFIG };
  let adjust: PoseAdjust = { ...NO_ADJUST };
  let customBg: ImageBitmap | null = null;
  let customBgBlob: Blob | null = null;
  const mySlot = () => (isHost ? 0 : 1);
  const partnerSlot = () => (isHost ? 1 : 0);
  let partnerName = '';
  const nameOf = (id: string) => (id === room.selfId ? 'Bạn' : partnerName || 'Bạn kia');
  const shortName = (n: string) => n.trim().split(/\s+/).slice(-1)[0] || n;

  // ---------------------------------------------------------------- stage
  const bgCanvas = h('canvas', { width: STAGE_W, height: STAGE_H });
  const tint = h('div.bth-tint');
  const redrawBg = () => {
    drawBackground(bgCanvas.getContext('2d')!, config.background, STAGE_W, STAGE_H, customBg);
    stage.setBackground(bgCanvas);
    stage.sceneSlot = sceneSlotOf(config);
    // my camera is the scene: shown whole, so my phone does not need to cut me out
    if ('segment' in pipe) (pipe as { segment: boolean }).segment = stage.sceneSlot !== mySlot();
    stage.setFilter(filterById(config.filter).css);
    tint.style.background = filterById(config.filter).tint ?? 'transparent';
  };
  const local: StageLayer = { id: 'local', source: pipe.packed, face: DEFAULT_FACE, adjust, slot: mySlot(), mirrored: true, noFace: false,
    peerId: room.selfId, poses: pipe.poses, seq: () => pipe.packedSeq, frameId: () => pipe.frameId };
  stage.layers = [local];
  const remoteVideo = h('video', { muted: true, playsInline: true, autoplay: true });
  const remoteAudio = h('audio', { autoplay: true });
  let remote: StageLayer | null = null;

  // ---------------------------------------------------------------- layout state (host-owned, versioned)
  const sync = new LayoutSync(room, isHost, room.selfId);
  let layout: LayoutState = sync.state;
  let lastIt: InteractionState | null = null;
  let armed = false;
  let mode: 'live' | 'shoot' = 'live';
  let dragHint = true;
  let progressText = '';
  let lastSig = '';

  // ---------------------------------------------------------------- DOM: stage overlays
  const flashEl = h('div.bth-flash');
  const countEl = h('div.bth-count', { role: 'timer', 'aria-live': 'assertive' });
  const ldot = h('span.ldot');
  const partnerText = h('span.clip', {}, 'Đang chờ bạn…');
  const statsBox = h('div.bth-statsbox', { hidden: true, role: 'note' });
  const partnerPill = h('button.bth-pill.partner', { 'aria-label': 'Kết nối', 'aria-expanded': 'false' }, ldot, partnerText);
  partnerPill.onclick = () => {
    statsBox.hidden = !statsBox.hidden;
    partnerPill.setAttribute('aria-expanded', String(!statsBox.hidden));
  };
  const photosPill = h('button.bth-pill', { hidden: true, 'aria-label': 'Ảnh trong phòng' }, icon('photos', 16, 2));
  const codePill = h('button.bth-pill.code', { 'aria-label': `Mã phòng ${req.code}, chạm để mời` }, req.code);
  codePill.onclick = () => void shareInvite(req.code).then(() => toast('Đã copy/chia sẻ lời mời'), (e) => toast('Không chia sẻ được: ' + (e as Error).message));
  const backPill = h('button.bth-pill.round', { 'aria-label': 'Rời phòng' }, icon('back', 18, 2.2));
  const liveTop = h('div.bth-top', {}, h('div.grp', {}, backPill, partnerPill), h('div.grp', {}, photosPill, codePill));
  const syncPill = h('div.bth-pill', {}, h('span.ldot.ok'), h('span.clip', {}, 'đồng bộ'));
  const kieuPill = h('div.bth-pill.brand', {}, 'Kiểu 1 / 4');
  const shootTop = h('div.bth-top', { hidden: true }, syncPill, kieuPill);

  const lockPill = h('div.bth-pill.warn', { hidden: true, role: 'status' });
  const coachPill = h('div.bth-pill', { hidden: true, role: 'status', 'aria-live': 'polite' });
  const infoPill = h('div.bth-pill.bth-fade', { hidden: true });
  const readyCheck = h('div.bth-check', { hidden: true, 'aria-label': 'Mọi người đã vào tư thế' }, icon('check', 22, 2.6));
  const retryCam = h('button.bth-soft', {}, icon('retry', 16, 2), 'Thử lại');
  retryCam.onclick = () => { retryCam.disabled = true; void remount(); };
  const camErrEl = h('div.bth-camerr', { hidden: !camError },
    icon('camOff', 34, 1.7), h('b', {}, 'Không mở được camera'),
    h('p', {}, (camError ?? '') + ' · Kiểm tra quyền camera rồi thử lại. Bạn vẫn thấy được người kia.'), retryCam);

  const stageEl = h('div.bth-stage', {},
    stage.canvas, tint, camErrEl, countEl, flashEl, liveTop, shootTop, statsBox, readyCheck,
    h('div.bth-bottom', {}, lockPill, coachPill, infoPill));

  // ---------------------------------------------------------------- DOM: controls
  const micBtn = h('button.bth-cbtn', { 'aria-label': 'Tắt mic', 'aria-pressed': 'false' }, icon('mic'));
  if (!cam.audio) { micBtn.disabled = true; micBtn.setAttribute('aria-label', 'Không có micro'); micBtn.replaceChildren(icon('micOff')); }
  micBtn.onclick = () => {
    if (!cam.audio) return;
    cam.audio.enabled = !cam.audio.enabled;
    const off = !cam.audio.enabled;
    micBtn.classList.toggle('off', off);
    micBtn.replaceChildren(icon(off ? 'micOff' : 'mic'));
    micBtn.setAttribute('aria-label', off ? 'Bật mic' : 'Tắt mic');
    micBtn.setAttribute('aria-pressed', String(off));
  };
  const flipBtn = h('button.bth-cbtn', { 'aria-label': 'Đổi camera', disabled: !!camError }, icon('flip'));
  flipBtn.onclick = async () => {
    flipBtn.disabled = true;
    try {
      const facing = await cam.switchFacing();
      pipe.sourceChanged(facing === 'user');
      toast(facing === 'user' ? 'Camera trước' : 'Camera sau – quay khung cảnh xung quanh bạn');
    } catch (e) {
      toast('Không đổi được camera: ' + (e as Error).message);
    } finally {
      flipBtn.disabled = false;
    }
  };
  const ring = h('span.ring');
  const shutter = h('button.bth-shutter', { class: isHost ? '' : 'guest', 'aria-label': isHost ? 'Bắt đầu chụp' : 'Chờ chủ phòng bấm chụp' }, ring);
  const fxBtn = h('button.bth-cbtn', { 'aria-label': 'Hiệu ứng', 'aria-expanded': 'false', 'aria-controls': 'bth-sheet' }, icon('sparkle'));
  const inviteBtn = h('button.bth-cbtn', { 'aria-label': 'Mời bạn' }, icon('invite'));
  inviteBtn.onclick = async () => {
    inviteBtn.disabled = true;
    try {
      if (req.room) await inviteFriendsTo(req.room.id, req.room.members.map((m) => m.user.id));
      else { await shareInvite(req.code); toast('Đã copy/chia sẻ lời mời'); }
    } catch (e) {
      toast('Không gửi được lời mời: ' + (e as Error).message);
    } finally {
      inviteBtn.disabled = false;
    }
  };
  const controls = h('div.bth-controls', {}, micBtn, flipBtn, shutter, fxBtn, inviteBtn);

  // ---------------------------------------------------------------- DOM: shooting
  const tray = h('div.bth-tray', { 'aria-label': 'Các kiểu đã chụp' });
  const stopBtn = h('button.bth-stop', { 'aria-label': 'Dừng chụp', disabled: !isHost }, h('span'));
  const shootCap = h('span.bth-shootcap', { 'aria-live': 'polite' });
  const shootCtl = h('div.bth-shootctl', {}, stopBtn, shootCap);

  // ---------------------------------------------------------------- DOM: effects sheet
  const tabsEl = h('div.bth-tabs', { role: 'tablist', 'aria-label': 'Hiệu ứng' });
  const panel = h('div.bth-panel', { role: 'tabpanel' });
  const sheet = h('section#bth-sheet.bth-sheet', { hidden: true, 'aria-label': 'Hiệu ứng' },
    h('button.bth-handle', { 'aria-label': 'Đóng hiệu ứng', onclick: () => setSheet(false) }), tabsEl, panel);
  const setSheet = (open: boolean) => {
    sheet.hidden = !open;
    fxBtn.classList.toggle('on', open);
    fxBtn.setAttribute('aria-expanded', String(open));
    if (open) renderPanel();
  };
  fxBtn.onclick = () => setSheet(sheet.hidden);

  // ---------------------------------------------------------------- result screen
  const saveStates = new Map<string, SaveState>();
  let storedSession: string | null = null;
  const result = createResult({
    isHost,
    savedRoom: !!req.room && isLoggedIn(),
    items: () => gallery.items,
    state: (id) => saveStates.get(id) ?? (req.room ? { kind: 'saving' } : { kind: 'quick' }),
    post: async (id) => {
      const s: Session = await api.session(id);
      shareDialog(s);
    },
    open: (it) => openItem(it),
    again: () => { /* back to the live booth */ },
  });
  const setSave = (id: string, s: SaveState) => {
    saveStates.set(id, s);
    if (result.open && result.session === id) result.render();
  };
  const markStored = (sessionId: string) => {
    storedSession = sessionId;
    void awaitFinal(sessionId);
  };
  /**
   * v0.9: the server makes the final photos (GPU cut-out + relight). The result screen waits for
   * them (≤ 45 s), then shows the server's pictures instead of the phone's provisional ones.
   */
  const awaitFinal = async (sessionId: string) => {
    const since = Date.now();
    setSave(sessionId, { kind: 'finishing', since });
    const tick = window.setInterval(() => { if (result.open && result.session === sessionId) result.render(); }, 1000);
    try {
      let s: Session | null = null;
      while (Date.now() - since < 45_000) {
        try { s = await api.session(sessionId); } catch { s = null; }
        if (s && s.ready !== false) break;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (s?.ready) {
        // strip first (the big picture), then the single shots
        const photos = [...s.photos].sort((a, b) => (a.kind === 'STRIP' ? -1 : 0) - (b.kind === 'STRIP' ? -1 : 0));
        for (const p of photos) {
          try {
            const url = await finalUrl(p, (await remoteConfig())?.paywall !== false);
            gallery.useServerImage(sessionId, p.kind === 'STRIP' ? 'strip' : 'shot', p.index, url);
          } catch { /* keep the provisional picture of this one */ }
        }
      }
    } finally {
      clearInterval(tick);
      setSave(sessionId, { kind: 'saved' });
    }
  };

  const booth = h('main.bth', { 'data-mode': 'live' },
    h('div.bth-stagewrap', {}, stageEl), controls, tray, shootCtl, sheet, result.el, remoteAudio);
  root.replaceChildren(booth);
  const syncAr = () => booth.style.setProperty('--ar', String(stage.canvas.width / stage.canvas.height));
  syncAr();
  redrawBg();
  stage.start();
  const wake = await (navigator as any).wakeLock?.request('screen').catch(() => null);

  // ---------------------------------------------------------------- background sources
  let catalog: { id: string; name: string; url: string }[] = [];
  if (req.room && isHost) {
    void api.backgrounds().then((list) => {
      catalog = list;
      if (catalog.length && activeTab === 'bg') renderPanel();
    }).catch(() => { /* optional library; presets still work */ });
  }

  /** Host picked an image (upload / link / catalog) → normalise, show, share. */
  async function useCustomImage(src: Blob, label: string) {
    const t = toastProgress(`Đang dùng phông: ${label}…`);
    try {
      const bmp = await createImageBitmap(src);
      const c = h('canvas', { width: BG_W, height: BG_H });
      drawCover(c.getContext('2d')!, bmp, BG_W, BG_H);
      customBgBlob = await new Promise<Blob>((r, j) => c.toBlob((b) => (b ? r(b) : j(new Error('không mã hoá được ảnh'))), 'image/jpeg', 0.9));
      customBg = await createImageBitmap(customBgBlob);
      await room.sendCustomBg(customBgBlob);
      update({ background: 'custom', customBgRev: config.customBgRev + 1 });
      t.done(`Đã dùng phông: ${label}`);
    } catch (e) {
      t.fail(`Không dùng được ảnh này (${(e as Error).message})`);
    }
  }

  /**
   * Image from a link, fetched by the browser itself (works when the site allows it).
   * There is deliberately no server-side "fetch any URL" proxy (SSRF risk).
   */
  async function pasteLink() {
    const url = prompt('Dán đường link ảnh (https://…)');
    if (!url) return;
    try {
      if (!/^https:\/\//.test(url)) throw new Error('chỉ nhận link https://');
      const blob = await (await fetch(url, { mode: 'cors', credentials: 'omit' })).blob();
      if (!blob.type.startsWith('image/')) throw new Error('link không phải ảnh');
      await useCustomImage(blob, 'ảnh từ link');
    } catch (e) {
      toast('Không lấy được ảnh từ link này (' + (e as Error).message + '). Hãy lưu ảnh về máy rồi chọn "Ảnh của bạn".', 5000);
    }
  }

  // ---------------------------------------------------------------- sheet tabs
  type TabId = 'beauty' | 'face' | 'filter' | 'bg' | 'frame' | 'layout';
  const TABS: [TabId, string, () => HTMLElement][] = [
    ['beauty', 'Làm đẹp', beautyPanel],
    ['face', 'Hiệu ứng mặt', facePanel],
    ['filter', 'Bộ lọc', filterPanel],
    ['bg', 'Nền', backgroundPanel],
    ['frame', 'Khung', framePanel],
    ['layout', 'Bố cục', layoutPanel],
  ];
  let activeTab: TabId = 'filter';
  function renderPanel() {
    if (sheet.hidden) return;
    tabsEl.replaceChildren(...TABS.map(([id, label]) => h('button.bth-tab', {
      role: 'tab', 'aria-selected': String(id === activeTab),
      onclick: () => { activeTab = id; panel.scrollTop = 0; renderPanel(); },
    }, label)));
    const top = panel.scrollTop;
    panel.replaceChildren(TABS.find(([id]) => id === activeTab)![2]());
    panel.scrollTop = top;
  }

  const roNote = () => (isHost ? null : h('p.bth-note', {}, 'Chủ phòng chọn giao diện · bạn vẫn tự kéo / phóng to chính mình được'));
  const wrap = (...kids: (Node | null | false)[]) => h('div', { class: isHost ? '' : 'bth-ro' }, roNote(), ...kids);
  const chips = (items: { id: string; label: string }[], cur: string, pick: (id: string) => void, enabled = isHost) =>
    h('div.bth-chips', {}, ...items.map((it) =>
      h('button.bth-chip', { class: it.id === cur ? 'on' : '', 'aria-pressed': String(it.id === cur), disabled: !enabled, onclick: () => pick(it.id) }, it.label)));
  const sw = (label: string, inner: { bg?: string; img?: string; filter?: string; icon?: Node }, on: boolean, pick: () => void, enabled = isHost) =>
    h('button.bth-sw', { title: label, class: on ? 'on' : '', 'aria-pressed': String(on), disabled: !enabled, onclick: pick },
      h('i', { style: {
        ...(inner.bg ? { background: inner.bg } : {}),
        ...(inner.img ? { backgroundImage: `url("${inner.img}")` } : {}),
        ...(inner.filter ? { filter: inner.filter } : {}),
      } }, inner.icon ?? ''),
      h('span', {}, label));
  /** "Halloween  MỚI · còn 34 ngày" */
  const packTitle = (p: FramePack) => h('h4', {}, p.name, h('span.bth-new', {}, `MỚI · còn ${packDaysLeft(p)} ngày`));

  // personal (not host-controlled): applied on this phone before the video leaves it,
  // so the partner, the shared preview and the stored photo all show the same face
  function beautyPanel() {
    const cur = pipe.beauty;
    const set = (next: BeautySettings) => { pipe.beauty = next; saveBeauty(next); };
    const presets = h('div.bth-presets', {}, ...BEAUTY_PRESETS.map((p) =>
      sw(p.name, { bg: p.swatch }, p.preset === cur.preset, () => {
        set({ preset: p.preset, smooth: p.smooth, bright: p.bright, eyes: p.eyes, slim: p.slim, lip: p.lip, blush: p.blush });
        renderPanel();
      }, true)));
    const sliders = h('div.bth-sliders', {}, ...SLIDERS.map(({ key, label }) => {
      const out = h('output', {}, String(cur[key]));
      const input = h('input.bth-range', {
        type: 'range', min: '0', max: '100', step: '1', value: String(cur[key]), 'aria-label': label,
        style: { '--v': `${cur[key]}%` },
      }) as HTMLInputElement;
      input.oninput = () => {
        const v = Number(input.value);
        input.style.setProperty('--v', `${v}%`);
        out.textContent = String(v);
        set({ ...pipe.beauty, [key]: v });
      };
      return h('label.bth-slider', {}, h('span', {}, label), input, out);
    }));
    // hold to see the camera without retouch
    const compare = h('button.bth-wide', { type: 'button' }, 'Giữ để so sánh với ảnh gốc');
    let saved: BeautySettings | null = null;
    const down = () => { saved = pipe.beauty; pipe.beauty = { ...saved, preset: 'off', smooth: 0, bright: 0, eyes: 0, slim: 0, lip: 0, blush: 0 }; };
    const up = () => { if (saved) { pipe.beauty = saved; saved = null; } };
    compare.addEventListener('pointerdown', down);
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) compare.addEventListener(ev, up);
    return h('div', {},
      h('p.bth-note', {}, 'Chỉ áp dụng cho bạn. Mọi người và ảnh lưu thấy đúng như bạn đang thấy.'),
      presets, sliders,
      h('div', { style: { marginTop: '16px' } }, compare));
  }

  // face-tracked stickers (personal, like the retouch): drawn into the outgoing frame
  let stickerPreviews: Map<string, string> | null = null;
  function previews() {
    if (stickerPreviews) return stickerPreviews;
    const atlas = h('canvas', { width: CELL * ATLAS_COLS, height: CELL * ATLAS_ROWS }) as HTMLCanvasElement;
    drawAtlas(atlas.getContext('2d')!);
    const cell = h('canvas', { width: 96, height: 96 }) as HTMLCanvasElement;
    const cx = cell.getContext('2d')!;
    stickerPreviews = new Map(STICKERS.map((s) => {
      cx.clearRect(0, 0, 96, 96);
      cx.drawImage(atlas, (s.cell % ATLAS_COLS) * CELL, Math.floor(s.cell / ATLAS_COLS) * CELL, CELL, CELL, 0, 0, 96, 96);
      return [s.id, cell.toDataURL('image/png')];
    }));
    return stickerPreviews;
  }
  function facePanel() {
    if (pipe instanceof PersonPipeline) {
      return h('p.bth-note', {}, 'Máy này chưa chạy được hiệu ứng bám mặt.');
    }
    const pv = previews();
    const pick = (id: string) => { pipe.sticker = id; saveSticker(id); renderPanel(); };
    return h('div', {},
      h('p.bth-note', {}, 'Bám theo khuôn mặt của bạn, mọi người và ảnh lưu đều thấy.'),
      h('div.bth-sws', {},
        sw('Không', { icon: icon('close', 22) }, pipe.sticker === 'none', () => pick('none'), true),
        ...STICKERS.map((s) => sw(s.name, { img: pv.get(s.id) }, pipe.sticker === s.id, () => pick(s.id), true))));
  }

  function filterPanel() {
    const sample = 'linear-gradient(135deg,#F7C9B6 0 45%,#FFD9E4 45% 70%,#9DB8FF 70%)';
    return wrap(h('div.bth-sws', {}, ...FILTERS.map((f) =>
      sw(f.name, { bg: sample, filter: f.css }, f.id === config.filter, () => update({ filter: f.id as BoothConfig['filter'] })))));
  }

  function backgroundPanel() {
    const file = h('input', { type: 'file', accept: 'image/*', hidden: true });
    file.onchange = () => { const f = file.files?.[0]; if (f) void useCustomImage(f, f.name); };
    // "come into my scene": any member's real surroundings (travel, a café, home…)
    const members = [{ slot: mySlot(), name: 'Bạn' }, ...(partnerName ? [{ slot: partnerSlot(), name: partnerName }] : [])]
      .sort((a, b) => a.slot - b.slot);
    const sceneOn = (slot: number) => sceneSlotOf(config) === slot;
    const special = h('div.bth-sws', {},
      ...members.map((m) => sw(`Cảnh của ${m.name}`, { icon: icon(m.slot === 0 ? 'home' : 'scene', 24) }, sceneOn(m.slot),
        () => { update({ background: 'member-camera', sceneSlot: m.slot }); toast(`Mọi người vào khung cảnh của ${m.name}`); })),
      sw('Ảnh của bạn', { icon: icon('upload', 24) }, false, () => file.click()),
      sw('Dán link ảnh', { icon: icon('link', 24) }, false, () => void pasteLink()),
      ...(config.background === 'custom' && customBg ? [sw('Đang dùng', { icon: icon('check', 24) }, true, () => {})] : []),
      file);
    const lib = catalog.length
      ? h('div.bth-sws', {}, ...catalog.map((c) => sw(c.name, { img: c.url }, false, () => {
        const t = toastProgress(`Đang tải phông ${c.name}…`);
        void fetch(c.url).then((r) => { if (!r.ok) throw new Error(`lỗi ${r.status}`); return r.blob(); })
          .then((b) => { t.close(); return useCustomImage(b, c.name); })
          .catch((e) => t.fail(`Không tải được phông (${(e as Error).message})`));
      })))
      : null;
    const invite = !isHost
      ? h('div', {},
        h('button.bth-wide', {
          disabled: !partnerName || sceneOn(mySlot()),
          onclick: () => { room.sendSceneRequest({ slot: mySlot(), name: req.name }); toast('Đã gửi lời mời tới chủ phòng'); },
        }, icon('scene', 18), sceneOn(mySlot()) ? 'Mọi người đang ở trong cảnh của bạn' : 'Mời mọi người vào khung cảnh của tôi'),
        h('p.bth-note.sm', {}, 'Đang đi chơi? Đổi sang camera sau để quay cảnh, hoặc giữ camera trước để chụp cùng cảnh.'))
      : null;
    const seasonal = activePacks().flatMap((p) => {
      const items = SEASONAL_BACKGROUNDS.filter((b) => b.pack === p.id);
      return items.length ? [packTitle(p), h('div.bth-sws', {}, ...items.map((b) =>
        sw(b.name, { bg: b.swatch }, b.id === config.background, () => update({ background: b.id }))))] : [];
    });
    return h('div', {},
      invite,
      h('div', { class: isHost ? '' : 'bth-ro' }, roNote(),
        ...seasonal,
        h('h4', {}, 'Khung cảnh thật & ảnh riêng'), special,
        lib && h('h4', {}, 'Thư viện phông'), lib,
        h('h4', {}, 'Phông hệ thống'),
        h('div.bth-sws', {}, ...BACKGROUNDS.map((b) => sw(b.name, { bg: b.swatch }, b.id === config.background, () => update({ background: b.id }))))));
  }

  function framePanel() {
    const pickColor = (id: string) => update({ frameColor: id as BoothConfig['frameColor'] });
    const packs = activePacks().flatMap((p) => {
      const items = SEASONAL_FRAME_COLORS.filter((c) => c.pack === p.id);
      return items.length ? [packTitle(p), h('div.bth-sws', {}, ...items.map((c) => sw(c.name, { bg: c.bg }, c.id === config.frameColor, () => pickColor(c.id))))] : [];
    });
    const caption = h('input.bth-field', { value: config.caption, maxLength: 28, disabled: !isHost, placeholder: 'Dòng chữ dưới ảnh', 'aria-label': 'Dòng chữ dưới ảnh' });
    caption.onchange = () => update({ caption: caption.value });
    const stickers = h('label.bth-checkrow', {}, h('input', {
      type: 'checkbox', checked: config.stickers, disabled: !isHost,
      onchange: (e: Event) => update({ stickers: (e.target as HTMLInputElement).checked }),
    }), 'Sticker trang trí');
    return wrap(
      h('h4', {}, 'Kiểu dải ảnh'),
      chips(LAYOUTS.map((l) => ({ id: l.id, label: `${l.name} (${l.cells.length})` })), config.layout, (id) => update({ layout: id as BoothConfig['layout'] })),
      ...packs,
      h('h4', {}, 'Màu khung'),
      h('div.bth-sws', {}, ...FRAME_COLORS.map((c) => sw(c.name, { bg: c.bg }, c.id === config.frameColor, () => pickColor(c.id)))),
      h('h4', {}, 'Chữ dưới ảnh'), caption, stickers);
  }

  // --- Bố cục: template, order (drag / tap to swap), who is in front, photo mirroring, timer
  let selected: string | null = null;
  function layoutPanel() {
    const ids = orderOf(layout);
    const tpls = h('div.bth-tpls', {}, ...TEMPLATES.map((t) => h('button.bth-tpl', {
      class: t.id === layout.template ? 'on' : '', 'aria-pressed': String(t.id === layout.template), disabled: !isHost,
      onclick: () => setLayout(setTemplate(layout, t.id)),
    }, icon(t.id, 30, 1.6), t.name)));

    const order = h('div.bth-order', { 'aria-label': 'Thứ tự đứng từ trái sang phải' });
    ids.forEach((id, i) => {
      const n = nameOf(id);
      const ava = h('button.bth-ava', {
        class: (id === room.selfId ? '' : 'alt') + (selected === id ? ' sel' : ''), disabled: !isHost || ids.length < 2,
        'data-peer': id, 'aria-label': `${n}, vị trí ${i + 1}${isHost ? ' — kéo hoặc chạm để đổi chỗ' : ''}`,
      }, h('i', {}, (n === 'Bạn' ? req.name : n).trim().charAt(0).toUpperCase() || '?'),
      h('span', {}, n === 'Bạn' ? 'Bạn' : shortName(n)), h('em', {}, ids.length === 2 ? (i === 0 ? 'bên trái' : 'bên phải') : `#${i + 1}`));
      if (isHost && ids.length > 1) dragToSwap(ava, id);
      order.append(ava);
      if (i < ids.length - 1) {
        order.append(h('button.bth-swap', {
          'aria-label': `Đổi chỗ ${n} và ${nameOf(ids[i + 1])}`, disabled: !isHost,
          onclick: () => setLayout(swapSlots(layout, id, ids[i + 1])),
        }, icon('swap', 18, 2)));
      }
    });
    const front = frontOf(layout);
    const natural = layout.scale !== 'matched';
    return h('div', {},
      roNote(),
      h('div', { class: isHost ? '' : 'bth-ro' },
        h('h4', {}, 'Kích thước'),
        chips([{ id: 'natural', label: 'Tỉ lệ thật' }, { id: 'matched', label: 'Cùng cỡ mặt' }], natural ? 'natural' : 'matched',
          (id) => setLayout(setScale(layout, id as 'natural' | 'matched'))),
        h('p.bth-note.sm', {}, natural
          ? 'Giữ nguyên tỉ lệ camera: ai đứng gần camera hơn sẽ to hơn và đứng trước. Muốn bằng nhau thì đứng lùi ra xa vừa phải.'
          : 'Mặt mọi người được chỉnh về cùng một cỡ.'),
        h('h4', {}, 'Kiểu đứng'), tpls,
        h('h4', {}, 'Thứ tự (trái → phải)'), order,
        ids.length < 2 ? h('p.bth-note.sm', {}, 'Khi bạn kia vào phòng, kéo avatar hoặc chạm hai người để đổi chỗ.') : null,
        layout.template === 'shoulder' && ids.length > 1 ? [
          h('h4', {}, 'Ai quàng vai'),
          chips([{ id: '', label: 'Tự động' }, ...ids.map((id) => ({ id, label: id === room.selfId ? 'Bạn' : shortName(nameOf(id)) }))],
            layout.giver ?? '', (id) => setLayout(setGiver(layout, id || null))),
        ] : null,
        natural ? null : [
          h('h4', {}, 'Ai đứng trước'),
          chips([{ id: '', label: 'Tự động' }, ...ids.map((id) => ({ id, label: id === room.selfId ? 'Bạn' : shortName(nameOf(id)) }))],
            front ?? '', (id) => setLayout(setFront(layout, id || null)), isHost && ids.length > 1),
          h('p.bth-note.sm', {}, 'Tự động: ai bước lại gần camera hơn sẽ đứng trước.'),
        ],
        h('h4', {}, 'Ảnh lưu'),
        chips([{ id: 'mirror', label: 'Như gương' }, { id: 'plain', label: 'Không lật' }], layout.mirrorPhoto ? 'mirror' : 'plain',
          (id) => { setLayout(setMirrorPhoto(layout, id === 'mirror')); update({ mirrorPhoto: id === 'mirror' }); }),
        h('h4', {}, 'Hẹn giờ mỗi kiểu'),
        chips([3, 5, 7, 10].map((s) => ({ id: String(s), label: `${s} giây` })), String(config.countdown), (id) => update({ countdown: Number(id) }))),
      h('h4', {}, 'Của riêng bạn'),
      chips([{ id: 'ai', label: 'Tách nền AI' }, { id: 'greenscreen', label: 'Phông xanh thật' }], pipe.mode, (id) => {
        pipe.mode = id as typeof pipe.mode;
        toast(pipe.mode === 'ai' ? 'Tách nền AI' : 'Phông xanh thật');
        renderPanel();
      }, true),
      h('div', { style: { marginTop: '10px' } },
        h('button.bth-wide', { onclick: () => { adjust = { ...NO_ADJUST }; local.adjust = adjust; toast('Đã đặt lại vị trí của bạn'); } },
          icon('reset', 18), 'Đặt lại vị trí của bạn')));
  }

  /** Pointer drag (touch + mouse) or tap-tap to swap two people. */
  function dragToSwap(el: HTMLElement, id: string) {
    let start: { x: number; y: number } | null = null;
    let ghost: HTMLElement | null = null;
    let over: HTMLElement | null = null;
    const target = (x: number, y: number) => {
      const t = document.elementFromPoint(x, y)?.closest<HTMLElement>('.bth-ava');
      return t && t !== el && t.dataset.peer ? t : null;
    };
    el.addEventListener('pointerdown', (e) => {
      start = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointermove', (e) => {
      if (!start) return;
      if (!ghost && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 8) {
        ghost = h('div.bth-ghost', {}, el.querySelector('i')?.textContent ?? '');
        document.body.append(ghost);
        el.classList.add('drag');
      }
      if (!ghost) return;
      ghost.style.left = `${e.clientX}px`;
      ghost.style.top = `${e.clientY}px`;
      const t = target(e.clientX, e.clientY);
      if (t !== over) { over?.classList.remove('over'); over = t; over?.classList.add('over'); }
    });
    const end = (e: PointerEvent, cancelled: boolean) => {
      if (!start) return;
      const dragged = !!ghost;
      ghost?.remove(); ghost = null;
      el.classList.remove('drag');
      over?.classList.remove('over');
      const t = dragged && !cancelled ? target(e.clientX, e.clientY) : null;
      over = null; start = null;
      if (t) { selected = null; setLayout(swapSlots(layout, id, t.dataset.peer!)); return; }
      if (dragged || cancelled) return;
      // tap: select, then tap another person to swap
      if (selected && selected !== id) { const other = selected; selected = null; setLayout(swapSlots(layout, other, id)); }
      else { selected = selected === id ? null : id; renderPanel(); }
    };
    el.addEventListener('pointerup', (e) => end(e, false));
    el.addEventListener('pointercancel', (e) => end(e, true));
  }

  function update(patch: Partial<BoothConfig>) {
    if (!isHost) return;
    config = { ...config, ...patch };
    sync.broadcast(config);
    redrawBg();
    renderPanel();
  }

  function setLayout(s: LayoutState) {
    if (!isHost) return;
    sync.set(s, config);
  }

  function applyLayout(s: LayoutState) {
    layout = s;
    extras.setLayout(s);
    extras.setCoaching(s.template !== 'side');
    if (activeTab === 'layout') renderPanel();
    renderBottom();
  }

  // ---------------------------------------------------------------- coaching + pose lock (US3, US5)
  const lineFor = (id: string, text: string) => h('span', {}, h('b', {}, id === room.selfId ? 'Bạn' : shortName(nameOf(id))), ': ', text);
  const ready = () => readiness(layout, lastIt, extras.supported, (id) => (id === room.selfId ? 'Bạn' : shortName(nameOf(id))));

  function renderBottom() {
    const r = ready();
    const coaching = layout.template !== 'side';
    const prompt = TEMPLATES.find((t) => t.id === layout.template)!.prompt;
    const sig = JSON.stringify([mode, armed, r, coaching, dragHint, progressText, extras.supported && lastIt?.hints, lastIt?.depthHints, camError && !remote]);
    if (sig === lastSig) return;
    lastSig = sig;

    // lock: host pressed the shutter but not everybody is in pose yet
    lockPill.hidden = !(armed && !r.ready && mode === 'live');
    if (!lockPill.hidden) {
      const now = h('button.now', { onclick: () => void startShoot(true) }, 'Chụp luôn');
      lockPill.replaceChildren(h('div.bth-lock', {}, h('span', {}, 'Chờ mọi người vào tư thế · ', r.missing.join(' · ')), now));
    }

    // coaching hints per person, or the template prompt
    let coachKids: (Node | string)[] = [];
    let ok = false;
    if (mode === 'shoot') {
      coachKids = [prompt];
    } else if (coaching && lockPill.hidden) {
      if (!extras.supported) coachKids = [prompt];
      else if (r.ready) { ok = true; coachKids = [isHost ? 'Sẵn sàng! Bấm chụp nhé' : 'Sẵn sàng! Chờ chủ phòng bấm chụp']; }
      else if (lastIt) {
        const ids = orderOf(layout);
        const lines = ids.flatMap((id) => {
          const t = lastIt!.hints[id] || lastIt!.depthHints[id];
          return t ? [lineFor(id, t)] : [];
        });
        coachKids = lines.length ? [h('div.bth-coach', {}, ...lines)] : [prompt];
      } else coachKids = ['Đang nhận diện tư thế…'];
    }
    coachPill.hidden = coachKids.length === 0;
    coachPill.classList.toggle('ok', ok);
    coachPill.replaceChildren(...(ok ? [icon('check', 14, 2.6)] : []), ...coachKids);
    readyCheck.hidden = !(mode === 'live' && coaching && extras.supported && r.ready);

    // info: camera problem, transfer progress, gesture hint
    const info = progressText ||
      (camError && remote ? 'Camera của bạn chưa mở' : '') ||
      (mode === 'live' && !coaching && dragHint && !(camError && !remote) ? 'Kéo để di chuyển · chụm để phóng to' : '');
    infoPill.hidden = !info || (!coachPill.hidden && !progressText);
    if (!infoPill.hidden) {
      infoPill.replaceChildren(info);
      if (camError && remote && !progressText) infoPill.append(h('button.bth-soft', { style: { height: '24px', padding: '0 10px', fontSize: '12px' }, onclick: () => void remount() }, 'Thử lại'));
    }
    shutter.classList.toggle('armed', armed);
  }

  extras.onInteraction((it) => {
    const fresh = lastIt ? newContacts(lastIt.contacts, it.contacts) : it.contacts;
    lastIt = it;
    if (fresh.length && layout.template !== 'side') touchFeedback();
    if (armed && ready().ready) void startShoot(false);
    renderBottom();
  });

  sync.onChange(applyLayout);
  applyLayout(sync.state);

  // ---------------------------------------------------------------- shutter (tap = pose-locked, long-press = now)
  let holdTimer = 0;
  let holdFired = false;
  shutter.addEventListener('pointerdown', () => {
    unlockAudio();
    if (!isHost || mode !== 'live') return;
    holdFired = false;
    shutter.classList.add('holding');
    holdTimer = window.setTimeout(() => {
      holdFired = true;
      shutter.classList.remove('holding');
      buzz(20);
      void startShoot(true);
    }, HOLD_MS);
  });
  const release = () => { clearTimeout(holdTimer); shutter.classList.remove('holding'); };
  shutter.addEventListener('pointerup', release);
  shutter.addEventListener('pointerleave', release);
  shutter.addEventListener('pointercancel', release);
  shutter.onclick = () => {
    if (holdFired) { holdFired = false; return; }
    if (!isHost) { toast('Chủ phòng sẽ bấm chụp – bạn cứ tạo dáng nhé'); return; }
    if (shoot.running || mode !== 'live') return;
    if (armed) { armed = false; renderBottom(); return; }
    if (ready().ready) void startShoot(false);
    else { armed = true; buzz(15); renderBottom(); }
  };

  // ---------------------------------------------------------------- gestures: move / zoom yourself
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchStart = 0, zoomStart = 1;
  stage.canvas.addEventListener('pointerdown', (e) => {
    stage.canvas.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (dragHint) { dragHint = false; renderBottom(); }
    if (!statsBox.hidden) { statsBox.hidden = true; partnerPill.setAttribute('aria-expanded', 'false'); }
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchStart = Math.hypot(a.x - b.x, a.y - b.y);
      zoomStart = adjust.zoom;
    }
  });
  stage.canvas.addEventListener('pointermove', (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    const r = stage.canvas.getBoundingClientRect();
    if (pointers.size === 1) {
      adjust = {
        ...adjust,
        dx: clamp(adjust.dx + (e.clientX - prev.x) / r.width, -0.45, 0.45),
        dy: clamp(adjust.dy + (e.clientY - prev.y) / r.height, -0.4, 0.4),
      };
    }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2 && pinchStart) {
      const [a, b] = [...pointers.values()];
      adjust = { ...adjust, zoom: clamp((zoomStart * Math.hypot(a.x - b.x, a.y - b.y)) / pinchStart, 0.6, 1.8) };
    }
    local.adjust = adjust;
  });
  const up = (e: PointerEvent) => { pointers.delete(e.pointerId); if (pointers.size < 2) pinchStart = 0; };
  stage.canvas.addEventListener('pointerup', up);
  stage.canvas.addEventListener('pointercancel', up);
  stage.canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    adjust = { ...adjust, zoom: clamp(adjust.zoom * (e.deltaY < 0 ? 1.05 : 0.95), 0.6, 1.8) };
    local.adjust = adjust;
  }, { passive: false });

  // ---------------------------------------------------------------- room gallery
  const gallery = new RoomGallery(() => customBg);
  const viewers = new Map<string, Awaited<ReturnType<typeof openViewer>>>();
  async function openItem(it: GalleryItem) {
    try {
      const v = await openViewer({
        title: it.kind === 'strip' ? 'Dải ảnh của cả phòng' : `Kiểu ${it.index + 1}`,
        previewUrl: it.previewUrl,
        photo: it.photo,
        filename: `booth-${req.code}-${it.kind}${it.index + 1}.jpg`,
        offlineNote: !it.photo && saveStates.get(it.sessionId)?.kind !== 'saved'
          ? 'Ảnh chưa lưu lên máy chủ – chỉ tải được bản xem trước. Đăng nhập và chụp trong phòng đã lưu để có HD / Ultra.' : undefined,
      });
      viewers.set(it.key, v);
    } catch (e) {
      toast('Không mở được ảnh: ' + (e as Error).message);
    }
  }
  const latestSession = () => gallery.items.find((i) => i.kind === 'strip')?.sessionId ?? gallery.items[0]?.sessionId ?? null;
  photosPill.onclick = () => { const s = latestSession(); if (s) result.show(s); };
  gallery.onChange = () => {
    photosPill.hidden = gallery.items.length === 0;
    photosPill.setAttribute('aria-label', `Ảnh trong phòng (${gallery.items.length})`);
    fillTray();
    if (result.open) result.render();
    // a viewer that is open without a server copy gets upgraded when it arrives
    for (const it of gallery.items) {
      const v = viewers.get(it.key);
      if (!v?.el.isConnected) viewers.delete(it.key);
      else if (it.photo) v.update({ photo: it.photo });
    }
  };
  // when the strip is ready everybody sees the result screen at the same time
  gallery.onStrip = (it) => {
    document.querySelectorAll('.viewer').forEach((v) => v.remove());
    if (!isHost) leaveShootMode();
    setSheet(false);
    result.show(it.sessionId);
  };

  // ---------------------------------------------------------------- shooting UI (BoothShoot)
  let trayTotal = 0;
  let traySession: string | null = null;
  let countdownRaf = 0;
  let guestEndTimer = 0;
  let lastSyncErr: number | null = null;
  function enterShootMode(total: number) {
    if (mode !== 'shoot') {
      mode = 'shoot';
      armed = false;
      booth.dataset.mode = 'shoot';
      liveTop.hidden = true;
      statsBox.hidden = true;
      shootTop.hidden = false;
      result.hide();
      lastSyncErr = null;
    }
    if (trayTotal !== total || traySession !== (shoot.info?.session ?? null)) {
      trayTotal = total;
      traySession = shoot.info?.session ?? null;
      tray.style.setProperty('--n', String(Math.min(total, 6)));
      tray.replaceChildren(...Array.from({ length: total }, (_, i) => h('div.bth-cell', { 'aria-label': `Kiểu ${i + 1}` })));
    }
    renderSyncPill();
    renderBottom();
  }
  function leaveShootMode() {
    if (mode === 'live') return;
    mode = 'live';
    booth.dataset.mode = 'live';
    liveTop.hidden = false;
    shootTop.hidden = true;
    cancelAnimationFrame(countdownRaf);
    countEl.textContent = '';
    shootCap.textContent = '';
    renderBottom();
  }
  function fillTray() {
    if (!traySession) return;
    for (const it of gallery.items) {
      if (it.sessionId !== traySession || it.kind !== 'shot' || !it.previewUrl) continue;
      const cell = tray.children[it.index] as HTMLElement | undefined;
      if (!cell) continue;
      cell.className = 'bth-cell done';
      cell.textContent = '';
      cell.style.backgroundImage = `url("${it.previewUrl}")`;
    }
  }
  function renderSyncPill() {
    const solo = !room.partnerId;
    const err = lastSyncErr != null ? Math.abs(lastSyncErr) : isHost ? null : room.lastStats?.clockErr ?? null;
    const dot = syncPill.querySelector('.ldot')!;
    dot.className = 'ldot ' + (solo || err == null ? 'ok' : err < 30 ? 'ok' : err < 80 ? '' : 'bad');
    syncPill.querySelector('.clip')!.textContent = solo ? 'Chụp một mình'
      : err == null || !Number.isFinite(err) ? 'đồng bộ · đang đo'
        : lastSyncErr != null ? `đồng bộ · lệch ${err.toFixed(0)}ms` : `đồng bộ · ±${err.toFixed(0)}ms`;
  }

  // ---------------------------------------------------------------- shooting
  let createFailed = false;
  let finalizeErr: string | null = null;
  const shoot = new ShootController(room, pipe, gallery, {
    countdown(target, index, total) {
      enterShootMode(total);
      clearTimeout(guestEndTimer);
      kieuPill.textContent = `Kiểu ${index + 1} / ${total}`;
      [...tray.children].forEach((c, i) => {
        const el = c as HTMLElement;
        if (i === index) { el.className = 'bth-cell now'; el.textContent = 'đang chụp'; el.style.backgroundImage = ''; }
        else if (el.classList.contains('now')) { el.className = 'bth-cell'; el.textContent = ''; }
      });
      cancelAnimationFrame(countdownRaf);
      const both = !!room.partnerId;
      const tick = () => {
        const left = target - performance.now();
        if (left <= 0) { countEl.textContent = ''; shootCap.textContent = both ? 'Hai máy cùng chụp…' : 'Đang chụp…'; return; }
        const n = Math.ceil(left / 1000);
        if (countEl.textContent !== String(n)) {
          countEl.textContent = String(n);
          countEl.classList.remove('pop');
          void countEl.offsetWidth;
          countEl.classList.add('pop');
          shootCap.textContent = both ? `Hai máy cùng chụp sau ${n} giây` : `Chụp sau ${n} giây`;
        }
        countdownRaf = requestAnimationFrame(tick);
      };
      tick();
    },
    flash() {
      flashEl.classList.remove('go');
      void flashEl.offsetWidth;
      flashEl.classList.add('go');
      buzz(30);
    },
    shotDone(index, syncErr) {
      const cell = tray.children[index] as HTMLElement | undefined;
      if (cell && !cell.style.backgroundImage) { cell.className = 'bth-cell done'; cell.textContent = 'đã chụp'; }
      if (syncErr != null) { lastSyncErr = syncErr; renderSyncPill(); }
      fillTray();
      // guest: leave the shooting screen even if the strip never arrives
      if (!isHost && index === trayTotal - 1) {
        clearTimeout(guestEndTimer);
        guestEndTimer = window.setTimeout(leaveShootMode, 8000);
      }
    },
    quiet: (until) => stage.quiet(until),
    status: (m) => {
      toast(m, 3500);
      if (m.startsWith('Không lưu được lên máy chủ')) createFailed = true;
      else if (m.startsWith('Lưu bộ ảnh lên máy chủ thất bại')) finalizeErr = m;
      else if (m.startsWith('Tải ảnh lên máy chủ thất bại')) {
        const id = shoot.info?.session;
        if (id && !isHost && saveStates.get(id)?.kind !== 'saved') setSave(id, { kind: 'error', msg: 'Ảnh của bạn chưa tải lên máy chủ được (mạng yếu?)' });
      }
    },
  }, () => adjust, mySlot, () => customBgBlob);
  shoot.serverRoomId = req.room?.id ?? null;
  // v0.9: wake the GPU compositor up now (its ~20 s start is over by the first photo)
  if (req.room?.id && isLoggedIn()) void api.warmRoom(req.room.id).catch(() => {});
  shoot.onStored = markStored;
  shoot.onStopped = () => {
    clearTimeout(guestEndTimer);
    leaveShootMode();
    toast('Chủ phòng đã dừng chụp');
  };
  // WYSIWYG: a compositor-aware ShootController may freeze the layout at the shutter
  const shootLayout = shoot as unknown as { setLayout?: (s: LayoutState) => void };
  sync.onChange((s) => shootLayout.setLayout?.(s));
  shootLayout.setLayout?.(layout);

  let runToken = 0;
  async function startShoot(force: boolean) {
    if (!isHost || shoot.running || mode !== 'live') return;
    if (!force && !ready().ready) return;
    armed = false;
    if (!room.partnerId && !confirm('Chưa có ai vào phòng. Chụp một mình?')) { renderBottom(); return; }
    const my = ++runToken;
    createFailed = false;
    finalizeErr = null;
    setSheet(false);
    const total = layoutById(config.layout).cells.length;
    enterShootMode(total);
    shootCap.textContent = 'Đang chuẩn bị…';
    const cfg = config;
    try {
      const info = await shoot.runHost(total, config.countdown, cfg);
      if (my !== runToken) return;
      const id = info.session;
      if (!req.room) setSave(id, { kind: 'quick' });
      else if (!info.key || createFailed) setSave(id, { kind: 'error', msg: 'Không lưu được lên máy chủ – chỉ có bản xem trước' });
      else if (storedSession !== id) setSave(id, { kind: 'error', msg: finalizeErr ?? 'Lưu bộ ảnh thất bại', retry: () => finalizeAgain(id, cfg) });
    } catch (e) {
      if (my !== runToken || e instanceof ShootCancelled) return;
      console.error(e);
      toast('Lỗi khi chụp: ' + (e as Error).message, 4000);
    } finally {
      if (my === runToken) leaveShootMode();
    }
  }

  /** Host: retry storing the session (uploads already happened). */
  async function finalizeAgain(id: string, cfg: BoothConfig) {
    setSave(id, { kind: 'saving' });
    try {
      const photos = (await api.finalize(id, cfg)).photos.map(toRef);
      gallery.setPhotos(photos);
      room.sendDone({ session: id, photos });
      markStored(id);
    } catch (e) {
      setSave(id, { kind: 'error', msg: 'Lưu bộ ảnh thất bại: ' + (e as Error).message, retry: () => finalizeAgain(id, cfg) });
      throw e;
    }
  }

  stopBtn.onclick = () => {
    if (!isHost) return;
    shoot.cancel();
    runToken++;
    leaveShootMode();
    toast('Đã dừng chụp');
  };
  if (!isHost) shootCap.title = 'Chủ phòng điều khiển việc chụp';

  // guest: save status of the host's sessions
  const guestTimers = new Map<string, number>();
  room.on('session', (info) => {
    if (isHost) return;
    if (!req.room) setSave(info.session, { kind: 'quick' });
    else if (!info.key) setSave(info.session, { kind: 'error', msg: 'Chủ phòng không lưu được lên máy chủ – chỉ có bản xem trước' });
    else {
      setSave(info.session, { kind: 'saving' });
      const wait = (info.total * (info.config.countdown + 3) + 60) * 1000;
      guestTimers.set(info.session, window.setTimeout(() => {
        if (saveStates.get(info.session)?.kind === 'saving') {
          setSave(info.session, { kind: 'error', msg: 'Chưa nhận được xác nhận lưu từ chủ phòng', retry: () => checkSaved(info.session) });
        }
      }, wait));
    }
  });
  async function checkSaved(id: string) {
    setSave(id, { kind: 'saving' });
    try {
      const s = await api.session(id);
      if (!s.photos.length) throw new Error('chưa lưu xong');
      gallery.setPhotos(s.photos.map(toRef));
      markStored(id);
    } catch (e) {
      setSave(id, { kind: 'error', msg: 'Chưa thấy bộ ảnh trên máy chủ: ' + (e as Error).message, retry: () => checkSaved(id) });
      throw e;
    }
  }

  // ---------------------------------------------------------------- network wiring
  pipe.onFace = (f) => (local.face = f);
  const poseTimer = setInterval(() => {
    local.mirrored = pipe.mirrored;
    local.noFace = pipe.noFace;
    const msg: PoseMsg = { face: pipe.face, adjust, mode: pipe.mode, fps: Math.round(pipe.fps), mirrored: pipe.mirrored, noFace: pipe.noFace };
    room.sendPose(msg);
  }, 66);

  const members = () => [{ id: room.selfId, name: req.name }, ...(room.partnerId ? [{ id: room.partnerId, name: partnerName || 'Bạn kia' }] : [])];
  room.on('partner', (p) => {
    partnerName = p?.name ?? '';
    ldot.className = 'ldot';
    partnerText.textContent = p ? `${shortName(p.name)} · …` : 'Đang chờ bạn…';
    if (isHost) {
      sync.set(withMembers(sync.state, members()), config); // broadcasts config + layout (late joiners)
      if (p) {
        sync.broadcast(config);
        if (customBgBlob && config.background === 'custom') void room.sendCustomBg(customBgBlob);
      }
    }
    if (!p && remote) {
      stage.layers = stage.layers.filter((l) => l !== remote);
      remote = null;
    }
    if (activeTab === 'bg' || activeTab === 'layout') renderPanel();
    renderBottom();
    toast(p ? `${p.name} đã vào phòng ♡` : 'Bạn kia đã rời phòng');
  });
  room.on('stream', (s) => {
    remoteVideo.srcObject = s;
    remoteAudio.srcObject = s;
    void remoteVideo.play().catch(() => {});
    void remoteAudio.play().catch(() => {});
    if (!remote) {
      const r: StageLayer = { id: 'remote', source: remoteVideo, face: DEFAULT_FACE, adjust: { ...NO_ADJUST }, slot: partnerSlot(), mirrored: true, noFace: false,
        peerId: room.partnerId ?? undefined, poses: room.remotePoses };
      remote = r;
      stage.layers = [local, r];
    }
    camErrEl.hidden = true; // the partner is visible even without our own camera
    renderBottom();
  });
  room.on('pose', (p) => {
    if (!remote) return;
    remote.face = p.face;
    remote.adjust = p.adjust;
    remote.mirrored = p.mirrored !== false;
    remote.noFace = !!p.noFace;
  });
  room.on('config', (c) => {
    const { config: plain, layout: l } = splitConfig(c);
    config = plain;
    if (l) sync.receive(l);
    redrawBg();
    renderPanel();
  });
  room.on('customBg', async (b) => {
    customBgBlob = b;
    customBg = await createImageBitmap(b);
    redrawBg();
  });
  room.on('full', () => {
    toast('Phòng đã đủ 2 người', 4000);
    void exit();
  });
  room.on('status', (s) => toast(s));
  room.on('progress', ({ what, pct }) => {
    progressText = pct < 1 ? `${what}… ${Math.round(pct * 100)}%` : '';
    renderBottom();
  });
  room.on('done', (d) => {
    gallery.setPhotos(d.photos);
    clearTimeout(guestTimers.get(d.session));
    markStored(d.session);
  });
  room.on('sceneRequest', (r) => {
    if (!isHost) return;
    if (confirm(`${r.name} mời mọi người vào khung cảnh của họ. Đồng ý?`)) {
      update({ background: 'member-camera', sceneSlot: r.slot });
      toast(`Đang chụp trong khung cảnh của ${r.name}`);
    }
  });
  room.on('stats', (s) => {
    const rtt = s.rttMs;
    ldot.className = 'ldot ' + (rtt < 150 ? 'ok' : rtt < 300 ? '' : 'bad');
    partnerText.textContent = `${shortName(partnerName || 'Bạn kia')} · ${rtt.toFixed(0)}ms`;
    const perf = extras.perf();
    const codec = s.codec ? `${s.codec}${s.encoderImpl ? ` (${s.encoderImpl})` : ''}` +
      (s.powerEfficient == null ? '' : s.powerEfficient ? ' · mã hoá phần cứng' : ' · mã hoá phần mềm') : 'codec: đang dò';
    const text =
      `RTT ${rtt.toFixed(0)} ms · ${s.relayed ? 'TURN relay' : 'P2P trực tiếp'} · ` +
      `${s.inFps.toFixed(0)} fps · ${(s.inKbps / 1000).toFixed(1)} Mbps · buffer ${s.jitterBufferMs.toFixed(0)} ms` +
      (isHost ? '' : ` · đồng hồ ±${Number.isFinite(s.clockErr) ? s.clockErr.toFixed(1) : '?'} ms`) +
      ` · AI ${pipe.fps.toFixed(0)} fps · khung ${pipe.frameMs.toFixed(1)} ms · chất lượng ${['cao', 'vừa', 'tiết kiệm'][pipe.level]} · ${codec}` +
      (perf ? ` · ghép ${perf.composeMs.toFixed(1)} ms · ML ${perf.mlMs.toFixed(1)} ms · ${perf.fps.toFixed(0)} fps` : '');
    partnerPill.title = text;
    statsBox.textContent = text;
    if (mode === 'shoot') renderSyncPill();
  });
  // dev-only (vite --mode mock): beacon real-device timings (numbers, no images) to scripts/mock-api.mjs
  let diagTimer = 0;
  if (import.meta.env.MODE === 'mock') {
    let longN = 0, longMs = 0;
    try {
      new PerformanceObserver((l) => { for (const e of l.getEntries()) { longN++; longMs += e.duration; } }).observe({ type: 'longtask', buffered: true });
    } catch { /* unsupported */ }
    diagTimer = window.setInterval(() => {
      const perf = stage.perf();
      void fetch('/api/_diag', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({
        t: Date.now(), ua: navigator.userAgent, dpr: devicePixelRatio, kind: pipe instanceof PersonPipeline ? 'legacy' : 'worker',
        fps: pipe.fps, frameMs: pipe.frameMs, mlMs: pipe.mlMs, level: pipe.level, frames: pipe.framesTotal,
        packed: `${pipe.packed.width}x${pipe.packed.height}`, stageFps: perf.fps, composeMs: perf.composeMs,
        longTasks: longN, longMs: Math.round(longMs), faceSeen: pipe.faceSeen, beauty: pipe.beauty, sticker: pipe.sticker,
        scale: layout.scale, stageRes: `${stage.canvas.width}x${stage.canvas.height}`,
        parts: (pipe as unknown as { parts?: unknown }).parts,
        cam: (() => { const t = cam.videoTrack(); const st = t.getSettings(); return { fps: st.frameRate, w: st.width, h: st.height, c: t.getConstraints() }; })(),
      }) }).catch(() => {});
      longN = 0; longMs = 0;
      // numbers only: no camera images ever leave the phone, not even in debug builds
    }, 2000);
  }
  const arTimer = setInterval(() => {
    syncAr();
    // read by the device tests (android/app/src/androidTest): camera → pipeline → stage are alive
    booth.dataset.camW = String(cam.video.videoWidth);
    booth.dataset.aiFrames = String(pipe.framesTotal);
    booth.dataset.stageFrames = String(stage.framesDrawn);
  }, 1000);

  backPill.onclick = () => void exit();

  // ---------------------------------------------------------------- teardown
  let exited = false;
  async function exit(notify = true) {
    if (exited) return;
    exited = true;
    shoot.cancel();
    clearInterval(poseTimer);
    clearInterval(arTimer);
    clearInterval(diagTimer);
    clearTimeout(guestEndTimer);
    guestTimers.forEach((t) => clearTimeout(t));
    cancelAnimationFrame(countdownRaf);
    stage.dispose();
    pipe.stop();
    cam.stop();
    void wake?.release().catch(() => {});
    document.querySelectorAll('.viewer, .bth-ghost').forEach((v) => v.remove());
    await room.leave().catch(() => {});
    if (notify) onExit();
  }
  addEventListener('pagehide', () => void room.leave(), { once: true });
  renderBottom();
  return () => exit(false);
}

/** getUserMedia errors in plain Vietnamese. */
function cameraErrorText(e: unknown) {
  const name = (e as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Bạn chưa cho phép dùng camera';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'Không tìm thấy camera trên máy này';
  if (name === 'NotReadableError' || name === 'AbortError') return 'Camera đang được ứng dụng khác dùng';
  return (e as Error)?.message || 'Không mở được camera';
}

/** A toast that says "working…" and then success / error (no silent failures). */
function toastProgress(msg: string) {
  const t = h('div.toast.show', { role: 'status' }, msg);
  document.body.append(t);
  const close = (after = 0) => setTimeout(() => { t.classList.remove('show'); setTimeout(() => t.remove(), 300); }, after);
  return {
    close: () => close(),
    done: (m: string) => { t.textContent = m; close(2200); },
    fail: (m: string) => { t.textContent = m; close(4500); },
  };
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
